import { moment } from "@msuite/utils/date";
import { z } from "@msuite/utils/lib/zod";
import { defineMutator, type Transaction } from "@rocicorp/zero";
import { Crews } from "../handlers/crews.handlers";
import { Guards } from "../handlers/guards.handlers";
import { Users } from "../handlers/users.handlers";
import type { ZeroContext } from "../types";
import { zql } from "../zql";

/**
 * Resolve a staffing row and verify the caller's organisation owns the crew it stands in.
 *
 * Every mutator in this file is a staffing write, and staffing belongs to the crew's owner alone —
 * not to the organisation whose plan the crew sits on. The guard therefore keys on the crew rather
 * than on the plan, which is what lets a partner organisation staff its own crew on a host's board
 * and what stops the host from staffing that same crew. Both halves are the same rule, and the
 * database enforces it too: `userAssignments` carries the crew's organisation and its write
 * policies compare `crews`."orgId".
 *
 * @param tx - The Zero transaction the mutator runs in.
 * @param ctx - The server-derived caller context.
 * @param assignmentId - The staffing row the mutation targets.
 * @returns The staffing row, so callers can reuse the read.
 */
async function assertStaffingOwnedByCaller(
	tx: Transaction,
	ctx: ZeroContext,
	assignmentId: string,
) {
	const assignment = await tx.run(zql.userAssignments.where("id", "=", assignmentId).one());
	if (!assignment) throw new Error("User assignment not found");
	await Guards.assertCrewOwnedByCaller(tx, ctx, assignment.crewId);
	return assignment;
}

export const userAssignmentMutators = {
	update: defineMutator(
		z.object({
			assignmentId: z.uuid(),
			ranking: z.number(),
			crewId: z.uuid(),
		}),
		async ({ args, tx, ctx }) => {
			// Both ends of the move: the crew the row leaves and the crew it arrives in have to
			// belong to the caller. Checking only the target would let one organisation pull another
			// organisation's people out of their slot.
			const assignment = await assertStaffingOwnedByCaller(tx, ctx, args.assignmentId);
			const crew = await Guards.assertCrewOwnedByCaller(tx, ctx, args.crewId);
			// A move stays on its day. The row's `planId` is not rewritten here, and it is what the
			// host's reads and the publish notification key on, so a crew on another day would leave
			// the person half on each; staffing another day is an insert, which clears that day first.
			if (crew.planId !== assignment.planId) throw new Error("Crew does not belong to the plan");
			await tx.mutate.userAssignments?.update({
				id: args.assignmentId,
				ranking: args.ranking,
				crewId: args.crewId,
			});
			await Crews.cleanCrewStaffRankings(tx, args.crewId);
			// A move between crews leaves a gap behind it, so the crew the person came from is
			// re-spaced too — the sequence invariant holds for every crew, not just the target.
			if (assignment.crewId !== args.crewId) {
				await Crews.cleanCrewStaffRankings(tx, assignment.crewId);
			}
		},
	),
	updateRanking: defineMutator(
		z.object({ assignmentId: z.uuid(), ranking: z.number() }),
		async ({ args, tx, ctx }) => {
			const assignment = await assertStaffingOwnedByCaller(tx, ctx, args.assignmentId);
			await tx.mutate.userAssignments?.update({
				id: args.assignmentId,
				ranking: args.ranking,
			});
			await Crews.cleanCrewStaffRankings(tx, assignment.crewId);
		},
	),
	delete: defineMutator(
		z.object({
			userAssignmentId: z.uuid(),
		}),
		async ({ tx, args, ctx }) => {
			const assignment = await assertStaffingOwnedByCaller(tx, ctx, args.userAssignmentId);
			await tx.mutate.userAssignments.delete({
				id: args.userAssignmentId,
			});
			await Crews.cleanCrewStaffRankings(tx, assignment.crewId);
		},
	),
	insert: defineMutator(
		z.object({
			// Ids must come from the call site: the mutator runs once on the client and
			// once on the server, and a generateId() in here yields two different rows —
			// the optimistic card is then torn down and remounted when the rebase lands.
			id: z.uuid(),
			userId: z.string(),
			crewId: z.uuid(),
			// Omitted, the person appends below the crew's current staffing. Callers pass a
			// ranking only to place relative to a neighbour, the way the drag paths do.
			ranking: z.number().optional(),
			planId: z.uuid(),
		}),
		async ({ args, tx, ctx }) => {
			const crew = await Guards.assertCrewOwnedByCaller(tx, ctx, args.crewId);
			if (crew.planId !== args.planId) throw new Error("Crew does not belong to the plan");
			// The crew is the caller's, which says nothing about the person going into it: the id
			// comes from the client and the row it writes is what the publish notification reads.
			await Guards.assertUserInOrg(tx, ctx, args.userId);
			await Users.cleanUserAssignments(tx, args.planId, args.userId);
			// Read after the clean: it may have removed this person from this very crew, and a
			// ranking resolved before that would sit above a row that no longer exists.
			const ranking = args.ranking ?? (await Users.getNextStaffRanking(tx, args.crewId));
			await tx.mutate.userAssignments.insert({
				id: args.id,
				userId: args.userId,
				crewId: args.crewId,
				ranking,
				planId: args.planId,
				// The crew's organisation, read from the row the guard just resolved. Named rather
				// than left to the column default, so the row's tenancy is decided by the guard that
				// admitted the write and not by the connection that carried it.
				orgId: crew.orgId,
				createdBy: ctx.userId,
				createdAt: moment().unix() as any,
			});
			await Crews.cleanCrewStaffRankings(tx, args.crewId);
		},
	),
};
